import type { HistSummary } from "../../types";

/**
 * The evaluation steps of processing_times in the order the backend runs them (pipeline.py evaluate_pizza):
 * sequential steps, one stage of side-by-side branches (phase 2 on the GPU pod while the burnt and blur CNNs
 * run on the backend) and the finer timings of a step as its details. Steps the layout does not know are
 * listed after it, so a new step shows up before anyone adds it here.
 */

export type StepStats = { step: string } & HistSummary;

interface StepNode {
  step: string;
  /** finer timings taken inside or alongside the step: shown under it, never added up */
  details?: string[];
}

type Stage = { kind: "step"; node: StepNode } | { kind: "parallel"; branches: StepNode[][] };

const STEP_LAYOUT: Stage[] = [
  { kind: "step", node: { step: "load_pizza_image" } },
  { kind: "step", node: { step: "calculate_crust" } },
  { kind: "step", node: { step: "pizza_presence" } },
  { kind: "step", node: { step: "calculate_pizza_properties", details: ["analyze_pizza_geometry"] } },
  {
    kind: "parallel",
    branches: [
      [{ step: "gpu_phase2", details: ["gpu_phase2_crop"] }],
      [{ step: "burnt_cnn", details: ["burnt_cnn_unwrap", "burnt_cnn_inference"] }, { step: "burnt_cnn_heatmap" }],
      [{ step: "blur_cnn_crop" }, { step: "blur_cnn_inference" }],
    ],
  },
  { kind: "step", node: { step: "evaluate_photo_quality", details: ["obstacle_seg_inference", "obstacle_seg_postprocess"] } },
  { kind: "step", node: { step: "slicing_cnn" } },
  { kind: "step", node: { step: "component_detection" } },
  { kind: "step", node: { step: "component_evaluation" } },
  { kind: "step", node: { step: "overall_evaluation" } },
];

export type StepRow =
  /** a step on the path; ``parallel`` = one of the side-by-side branches */
  | { kind: "step"; stats: StepStats; parallel: boolean }
  /** a finer timing of the step above */
  | { kind: "detail"; stats: StepStats; parallel: boolean }
  /** header of the side-by-side stage: it lasts as long as its longest branch */
  | { kind: "parallel"; avg: number; branches: number };

export interface StepBreakdown {
  rows: StepRow[];
  /** average time of the steps on the path, the side-by-side stage counted by its longest branch */
  accounted: number;
}

/** The measured steps laid out in pipeline order, with the time each one adds to the whole. */
export function stepBreakdown(steps: StepStats[]): StepBreakdown {
  const byStep = new Map(steps.map((s) => [s.step, s]));
  const placed = new Set<string>();
  const rows: StepRow[] = [];

  // rows of one step and its details; the step's average, or null when nothing of it was measured
  const nodeRows = (node: StepNode, parallel: boolean): { rows: StepRow[]; avg: number | null } => {
    const out: StepRow[] = [];
    const stats = byStep.get(node.step);
    placed.add(node.step);
    if (stats) out.push({ kind: "step", stats, parallel });
    for (const detail of node.details ?? []) {
      placed.add(detail);
      const detailStats = byStep.get(detail);
      if (detailStats) out.push({ kind: "detail", stats: detailStats, parallel });
    }
    return { rows: out, avg: out.length ? stats?.avg ?? 0 : null };
  };

  let accounted = 0;
  for (const stage of STEP_LAYOUT) {
    if (stage.kind === "step") {
      const node = nodeRows(stage.node, false);
      rows.push(...node.rows);
      accounted += node.avg ?? 0;
      continue;
    }
    const branchRows: StepRow[] = [];
    let longest = 0;
    let branches = 0;
    for (const branch of stage.branches) {
      let time: number | null = null;
      for (const step of branch) {
        const node = nodeRows(step, true);
        branchRows.push(...node.rows);
        if (node.avg !== null) time = (time ?? 0) + node.avg;
      }
      if (time !== null) {
        branches += 1;
        longest = Math.max(longest, time);
      }
    }
    if (branches > 0) {
      rows.push({ kind: "parallel", avg: longest, branches }, ...branchRows);
      accounted += longest;
    }
  }

  // unknown steps: most likely a new sequential step, counted as one
  for (const stats of steps.filter((s) => !placed.has(s.step)).sort((a, b) => (b.avg ?? 0) - (a.avg ?? 0))) {
    rows.push({ kind: "step", stats, parallel: false });
    accounted += stats.avg ?? 0;
  }
  return { rows, accounted };
}
